import { useState } from 'react';
import { Button, Group, Table, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { formatMoney } from '@erp/shared';
import { estimatesApi, type EstimateListItem } from '../api/estimates';
import { useAuth } from '../auth/AuthContext';
import { RegistryPage } from '../components/RegistryPage';

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU');
}

export function EstimatesPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canCreate = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);

  const list = useQuery({
    queryKey: ['estimates', { search: debouncedSearch }],
    queryFn: () => estimatesApi.list({ search: debouncedSearch }),
  });

  const items = list.data?.items ?? [];

  return (
    <RegistryPage
      title="Сметы"
      action={
        canCreate && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/estimates/new')}>
            Новая смета
          </Button>
        )
      }
      filters={
        <Group>
          <TextInput
            placeholder="Поиск по номеру, объекту или контрагенту"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={320}
          />
        </Group>
      }
    >
      <Table striped highlightOnHover verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Номер</Table.Th>
            <Table.Th>Дата</Table.Th>
            <Table.Th>Организация</Table.Th>
            <Table.Th>Заказчик</Table.Th>
            <Table.Th>Объект</Table.Th>
            <Table.Th>Сумма</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((estimate: EstimateListItem) => (
            <Table.Tr
              key={estimate.id}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/estimates/${estimate.id}`)}
            >
              <Table.Td>
                <Text size="sm" fw={500}>
                  № {estimate.number}
                </Text>
              </Table.Td>
              <Table.Td>{formatDate(estimate.date)}</Table.Td>
              <Table.Td>{estimate.organization.shortName}</Table.Td>
              <Table.Td>{estimate.counterparty.shortName}</Table.Td>
              <Table.Td>{estimate.objectName ?? '—'}</Table.Td>
              <Table.Td>{formatMoney(estimate.total)}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {!list.isLoading && items.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Сметы не найдены
        </Text>
      )}
    </RegistryPage>
  );
}
